import React, { useState } from "react";
import { 
  Box, 
  useTheme, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Typography,
  Menu,
  Grid,
  Divider
} from "@mui/material";
import { 
  useGetAdminsQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation
} from "state/api";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import CustomColumnMenu from "components/DataGridCustomColumnMenu";
import { Formik } from "formik";
import * as yup from "yup";
import toast, { Toaster } from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Validation schema - separate for create and edit
const createAdminSchema = yup.object().shape({
  firstName: yup.string().required("First name is required").trim(),
  lastName: yup.string().required("Last name is required").trim(),
  username: yup.string().required("Username is required").trim(),
  email: yup.string().email("Invalid email").required("Email is required").trim(),
  password: yup.string().min(5, "Password must be at least 5 characters").required("Password is required"),
  phoneNumber: yup.string().trim(),
  city: yup.string().trim(),
  state: yup.string().trim(),
  country: yup.string().trim(),
  occupation: yup.string().trim(),
  role: yup.string().required("Role is required")
});

const editAdminSchema = yup.object().shape({
  firstName: yup.string().required("First name is required").trim(),
  lastName: yup.string().required("Last name is required").trim(),
  username: yup.string().required("Username is required").trim(),
  email: yup.string().email("Invalid email").required("Email is required").trim(),
  password: yup.string().min(5, "Password must be at least 5 characters"),
  phoneNumber: yup.string().trim(),
  city: yup.string().trim(),
  state: yup.string().trim(),
  country: yup.string().trim(),
  occupation: yup.string().trim(),
  role: yup.string().required("Role is required")
});

const Admin = () => {
  const theme = useTheme();
  const { data, isLoading, refetch } = useGetAdminsQuery();
  const [createAdmin, { isLoading: isCreating }] = useCreateAdminMutation();
  const [updateAdmin, { isLoading: isUpdating }] = useUpdateAdminMutation();
  const [deleteAdmin] = useDeleteAdminMutation();

  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewingAdmin, setViewingAdmin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const initialValues = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    phoneNumber: "",
    city: "",
    state: "",
    country: "",
    occupation: "",
    role: "admin"
  };

      const handleMenuClick = (event, params) => {
      setAnchorEl(event.currentTarget);
      setSelectedRow(params.row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewOpen = () => {
    
    setViewingAdmin(selectedRow);
    setOpenViewDialog(true);
    handleMenuClose();
  };

  const handleEditOpen = () => {
    setEditingAdmin(selectedRow);
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDeleteOpen = () => {
    setAdminToDelete(selectedRow);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    
    
    setSubmitError(""); // Clear any previous errors
    
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Request timed out. Please check your connection and try again.")), 30000);
    });

    try {
      if (editingAdmin) {
        // Update admin
        const updateData = { ...values };
        if (!updateData.password || updateData.password.trim() === "") {
          delete updateData.password; // Don't update password if empty
        }
        const result = await Promise.race([
          updateAdmin({ id: editingAdmin._id, ...updateData }).unwrap(),
          timeoutPromise
        ]);
        toast.success("Administrator updated successfully!");
      } else {
        // Create admin - validate required fields
        if (!values.firstName?.trim()) {
          setFieldError("firstName", "First name is required");
          return;
        }
        if (!values.lastName?.trim()) {
          setFieldError("lastName", "Last name is required");
          return;
        }
        if (!values.username?.trim()) {
          setFieldError("username", "Username is required");
          return;
        }
        if (!values.email?.trim()) {
          setFieldError("email", "Email is required");
          return;
        }
        if (!values.password?.trim()) {
          setFieldError("password", "Password is required");
          return;
        }

        const adminData = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          username: values.username.trim(),
          email: values.email.trim(),
          password: values.password,
          phoneNumber: values.phoneNumber?.trim() || "",
          city: values.city?.trim() || "",
          state: values.state?.trim() || "",
          country: values.country?.trim() || "",
          occupation: values.occupation?.trim() || "",
          role: values.role || "admin"
        };

        const result = await Promise.race([
          createAdmin(adminData).unwrap(),
          timeoutPromise
        ]);
        toast.success("Administrator created successfully!");
      }
      
      setOpenDialog(false);
      setEditingAdmin(null);
      resetForm();
      refetch();
    } catch (error) {

      
      // Handle specific validation errors
      if (error?.data?.message) {
        const message = error.data.message.toLowerCase();
        if (message.includes("email already exists") || message.includes("email") && message.includes("use")) {
          setFieldError("email", "This email is already in use");
        } else if (message.includes("username already taken") || message.includes("username")) {
          setFieldError("username", "This username is already taken");
        } else {
          toast.error(error.data.message);
          setSubmitError(error.data.message);
        }
      } else if (error?.message) {
        toast.error(error.message);
        setSubmitError(error.message);
      } else {
        const errorMsg = "An unexpected error occurred. Please try again.";
        toast.error(errorMsg);
        setSubmitError(errorMsg);
      }
    } finally {

      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteAdmin(adminToDelete._id).unwrap();
      toast.success("Administrator deleted successfully!");
      setDeleteConfirmOpen(false);
      setAdminToDelete(null);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "An error occurred");
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'error';
      case 'admin':
        return 'primary';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 0.5,
    },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 0.5,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 0.5,
    },
    {
      field: "username",
      headerName: "Username",
      flex: 0.5,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      flex: 0.5,
      renderCell: (params) => {
        return params.value ? params.value.replace(/^(\d{3})(\d{3})(\d{4})/, "($1)$2-$3") : "N/A";
      },
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.4,
    },
    {
      field: "occupation",
      headerName: "Occupation",
      flex: 0.5,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.3,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={getRoleColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(event) => handleMenuClick(event, params)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Toaster position="top-right" />
      
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ADMINISTRATORS" subtitle="Managing administrators and list of administrators" />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingAdmin(null);
            setOpenDialog(true);
          }}
          sx={{
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.background.alt,
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
          }}
        >
          Add Administrator
        </Button>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={data || []}
          columns={columns}
          components={{
            ColumnMenu: CustomColumnMenu,
          }}
        />
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewOpen}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => {
        setOpenViewDialog(false);
        setViewingAdmin(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Administrator Details</DialogTitle>
        <DialogContent>
          {viewingAdmin && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>
                    {viewingAdmin.firstName} {viewingAdmin.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Username: {viewingAdmin.username}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Email: {viewingAdmin.email}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Phone: {viewingAdmin.phoneNumber || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>
                    Role: <Chip 
                      label={viewingAdmin.role} 
                      color={getRoleColor(viewingAdmin.role)}
                      size="small"
                      variant="outlined"
                    />
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Occupation: {viewingAdmin.occupation || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    City: {viewingAdmin.city || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    State: {viewingAdmin.state || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Country: {viewingAdmin.country || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Created: {viewingAdmin.createdAt ? new Date(viewingAdmin.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last Updated: {viewingAdmin.updatedAt ? new Date(viewingAdmin.updatedAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          )}
          {!viewingAdmin && (
            <Box>
              <Typography>Loading administrator details...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenViewDialog(false);
              setViewingAdmin(null);
            }}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.secondary.main,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAdmin ? "Edit Administrator" : "Add New Administrator"}
        </DialogTitle>
        <Formik
          initialValues={editingAdmin ? {
            ...editingAdmin,
            password: "" // Don't pre-fill password for editing
          } : initialValues}
          validationSchema={editingAdmin ? editAdminSchema : createAdminSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldError,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                {submitError && (
                  <Box mb={2}>
                    <Typography color="error" variant="body2">
                      {submitError}
                    </Typography>
                  </Box>
                )}
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: "span 1" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="First Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                    name="firstName"
                    error={!!touched.firstName && !!errors.firstName}
                    helperText={touched.firstName && errors.firstName}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Last Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                    name="lastName"
                    error={!!touched.lastName && !!errors.lastName}
                    helperText={touched.lastName && errors.lastName}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Username"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.username}
                    name="username"
                    error={!!touched.username && !!errors.username}
                    helperText={touched.username && errors.username}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    name="email"
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="password"
                    label={editingAdmin ? "New Password (leave blank to keep current)" : "Password"}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    name="password"
                    error={!!touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={values.role}
                      onChange={handleChange}
                      name="role"
                      error={!!touched.role && !!errors.role}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="superadmin">Super Admin</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Phone Number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phoneNumber}
                    name="phoneNumber"
                    error={!!touched.phoneNumber && !!errors.phoneNumber}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Occupation"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.occupation}
                    name="occupation"
                    error={!!touched.occupation && !!errors.occupation}
                    helperText={touched.occupation && errors.occupation}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="City"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.city}
                    name="city"
                    error={!!touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="State"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.state}
                    name="state"
                    error={!!touched.state && !!errors.state}
                    helperText={touched.state && errors.state}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Country"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.country}
                    name="country"
                    error={!!touched.country && !!errors.country}
                    helperText={touched.country && errors.country}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={() => {
                    setOpenDialog(false);
                    setSubmitError("");
                  }}
                  disabled={isSubmitting || isCreating || isUpdating}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={isSubmitting || isCreating || isUpdating}
                  sx={{
                    backgroundColor: theme.palette.secondary.light,
                    color: theme.palette.background.alt,
                  }}
                >
                  {(isSubmitting || isCreating || isUpdating) ? 
                    (editingAdmin ? "Updating..." : "Creating...") : 
                    (editingAdmin ? "Update" : "Create")
                  }
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete administrator "{adminToDelete?.firstName} {adminToDelete?.lastName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
